import { useEffect, useRef, useState } from "react";
import { Ban, MessageSquare, Send, Unlink, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Panel, Empty } from "@/components/common";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export function Chat({ contact, onChange }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [offline, setOffline] = useState(false);
  const endRef = useRef(null);
  const lastRef = useRef(null);
  useEffect(() => {
    setMsgs([]);
    lastRef.current = null;
    let alive = true;
    const pull = async () => {
      try {
        const { data } = await api.get(`/network/messages/${contact.id}`, { params: lastRef.current ? { since: lastRef.current } : {} });
        if (!alive) return;
        setOffline(false);
        if (!data.length) return;
        lastRef.current = data[data.length - 1].created_at;
        setMsgs((m) => [...m, ...data.filter((d) => !m.some((x) => x.id === d.id))]);
      } catch (err) {
        if (!alive) return;
        console.error("Network Soci chat sync failed", err);
        setOffline(true);
      }
    };
    pull();
    const t = setInterval(pull, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [contact.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/network/messages/${contact.id}`, { text });
      setText("");
      lastRef.current = data.created_at;
      setMsgs((m) => [...m, data]);
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChange(); } catch (e2) { toast.error(errMsg(e2)); } };
  return (
    <Panel className="flex h-[560px] flex-col" testId="netsoci-chat">
      <div className="flex items-center gap-3 border-b px-5 py-3">
        <UserAvatar name={contact.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold" data-testid="netsoci-chat-name">{contact.name}</p>
          <p className="truncate text-xs text-muted-foreground">{contact.role}{contact.company && ` · ${contact.company}`}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/network/connections/${contact.connection_id}`), "Collegamento interrotto")} data-testid="netsoci-disconnect-button"><Unlink className="mr-1.5 h-4 w-4" />Interrompi</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(() => api.post(`/network/block/${contact.id}`), "Contatto bloccato")} data-testid="netsoci-block-button"><Ban className="mr-1.5 h-4 w-4" />Blocca</Button>
      </div>
      {offline && <p className="border-b bg-destructive/10 px-5 py-1.5 text-xs text-destructive" data-testid="netsoci-chat-offline">Impossibile aggiornare la conversazione. Nuovo tentativo in corso…</p>}
      <div className="flex-1 space-y-2 overflow-y-auto bg-muted/30 px-5 py-4" data-testid="netsoci-messages">
        {msgs.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Nessun messaggio. Scrivi il primo.</p>}
        {msgs.map((m) => {
          const mine = m.from_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "border bg-card"}`} data-testid={`netsoci-message-${m.id}`}>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t p-3">
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio" className="min-h-[40px] resize-none"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} data-testid="netsoci-message-input" />
        <Button type="submit" disabled={!text.trim()} data-testid="netsoci-send-button"><Send className="h-4 w-4" /></Button>
      </form>
    </Panel>
  );
}

export function Contacts({ conns, onChange, initialChat }) {
  const [active, setActive] = useState(initialChat);
  const list = conns.accepted;
  const current = list.find((c) => c.id === active);
  if (!list.length) return <Empty icon={Users} text="Nessun collegamento. Cerca altri amministratori e invia una richiesta." testId="netsoci-contacts-empty" />;
  return (
    <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
      <Panel testId="netsoci-contacts">
        <ul className="divide-y">
          {list.map((c) => (
            <li key={c.id}>
              <button onClick={() => { setActive(c.id); onChange(); }} className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 ${active === c.id ? "bg-muted" : ""}`} data-testid={`netsoci-contact-${c.id}`}>
                <UserAvatar name={c.name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.company}</span>
                </span>
                {c.unread > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">{c.unread}</span>}
              </button>
            </li>
          ))}
        </ul>
      </Panel>
      {current ? <Chat contact={current} onChange={() => { setActive(null); onChange(); }} /> : (
        <Panel className="flex h-[560px] items-center justify-center"><p className="flex items-center gap-2 text-sm text-muted-foreground"><MessageSquare className="h-4 w-4" />Seleziona un collegamento per scrivergli</p></Panel>
      )}
    </div>
  );
}
